import type { MaybeRefOrGetter } from 'vue';
import type { AnyItem, CompositePrimaryKey, FieldSelection, RelationOperationToOne, RelationOperationToOneInCreate, RelationOperationToOneInUpdate, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { pickNonSpecialProps } from '@rstore/shared';
import { computed, toValue } from 'vue';
import { useDisplayTemplateFieldSelection } from '#layers/blocks/composables/use-display-template-field-selection';
import { useItems } from '#layers/collection-query/public';
import { CollectionReads, getCollectionPrimaryKeyFields, isConnectToOne, isCreateToOne, isDisconnectToOne, isEmptyKey, isItemMatchingKey, isUpdateToOne, mergeFieldSelections, pickByPrimitiveFields } from '#layers/data-model/public';
import { getOriginalItemKey, injectFormBlockContext } from '#layers/forms/public';

function findItemByKey(items: AnyItem[], key: CompositePrimaryKey) {
	return items.find((item) => isItemMatchingKey(item, key));
}

export interface UseM2OStateOptions {
	/**
	 * Extra field api names to fetch for the resolved item beyond the ones implied by the
	 * collection display template. Lets a caller (e.g. the user picker) render a richer selected
	 * value than the display template alone selects, without changing the display template itself.
	 */
	additionalSelection?: MaybeRefOrGetter<FieldSelection>;
}

export const useM2OState = (
	field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField | null>,
	referencedItemKey: MaybeRefOrGetter<CompositePrimaryKey | null>,
	fieldValue: MaybeRefOrGetter<RelationOperationToOne | null>,
	options: UseM2OStateOptions = {},
) => {
	const collection = computed(() => toValue(field)?.oppositeCollection);
	const formBlockContext = injectFormBlockContext(null);
	const isCreateForm = computed(() => formBlockContext?.isCreate.value ?? false);

	const effectiveKey = computed<CompositePrimaryKey | null>(() => {
		let key = toValue(referencedItemKey);

		if (isCreateForm.value) {
			const _fieldValue = toValue(fieldValue) as RelationOperationToOneInCreate | null;

			if (_fieldValue && '_connect' in _fieldValue) {
				key = _fieldValue._connect.key;
			}
			else if (_fieldValue && 'disconnect' in _fieldValue) {
				key = null;
			}
		}
		else {
			const _fieldValue = toValue(fieldValue) as RelationOperationToOneInUpdate | null;
			const connectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isConnectToOne(op)) : null;
			const disconnectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isDisconnectToOne(op)) : null;

			if (disconnectOperation) {
				key = null;
			}

			if (connectOperation) {
				key = connectOperation._connect.key;
			}
		}

		return key;
	});

	const isValidKey = computed(() => !isEmptyKey(effectiveKey.value));

	const filter = computed(() => {
		const key = effectiveKey.value;

		if (!key || isEmptyKey(key)) {
			return;
		}

		return {
			_and: Object.keys(key).map((keyName: string) => {
				return { [keyName]: { _eq: key[keyName] } };
			}),
		};
	});

	const fieldSelection = useDisplayTemplateFieldSelection(collection, CollectionReads.Many);
	const combinedFieldSelection = computed<FieldSelection>(() =>
		mergeFieldSelections(fieldSelection.value, toValue(options.additionalSelection) ?? { fields: [] }),
	);

	const { items, isLoading: isItemsLoading, refresh } = useItems({
		enabled: isValidKey,
		collection,
		query: () => ({ filter: filter.value, ...combinedFieldSelection.value }),
	});

	const isResolvingConnection = computed(() => {
		if (!isCreateForm.value || !collection.value) {
			return false;
		}

		const value = toValue(fieldValue) as RelationOperationToOneInCreate | null;

		if (!value || !isConnectToOne(value)) {
			return false;
		}

		return !findItemByKey(items.value, value._connect.key);
	});

	const isLoading = computed(() => isItemsLoading.value || isResolvingConnection.value);

	// Autorefresh on submit - fetch fresh data after save
	if (formBlockContext) {
		formBlockContext.state.value?.$onSuccess?.(() => {
			refresh();
		});
	}

	const item: AnyItem = computed(() => {
		if (!toValue(fieldValue)) {
			if (isValidKey.value) {
				return items.value[0];
			}
			else {
				return null;
			}
		}

		// In-Create
		if (isCreateForm.value) {
			const _fieldValue: RelationOperationToOneInCreate | null = toValue(fieldValue) as RelationOperationToOneInCreate;

			if (isCreateForm.value && isCreateToOne(_fieldValue)) {
				return _fieldValue._create.data;
			}
			else if (isConnectToOne(_fieldValue)) {
				return findItemByKey(items.value, _fieldValue._connect.key);
			}
		}

		// In-Update
		else {
			const _fieldValue: RelationOperationToOneInUpdate = toValue(fieldValue) as RelationOperationToOneInUpdate ?? [];

			const createOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isCreateToOne(op)) : null;
			const connectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isConnectToOne(op)) : null;
			const updateOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isUpdateToOne(op)) : null;
			const disconnectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isDisconnectToOne(op)) : null;

			let finalItem = items.value[0] ?? null;

			if (createOperation) {
				finalItem = createOperation._create.data;
			}

			if (updateOperation) {
				finalItem = { ...pickNonSpecialProps(finalItem), ...updateOperation._update.data };
			}

			if (disconnectOperation && !createOperation && !connectOperation) {
				finalItem = null;
			}

			return finalItem;
		}
	});

	const isCreate = computed(() => {
		const _fieldValue = toValue(fieldValue);
		return Array.isArray(_fieldValue) ? _fieldValue.some((op) => isCreateToOne(op)) : isCreateToOne(toValue(fieldValue));
	});
	const isUpdate = computed(() => {
		const _fieldValue = toValue(fieldValue);
		return Array.isArray(_fieldValue) ? _fieldValue.some((op) => isUpdateToOne(op)) : isUpdateToOne(toValue(fieldValue));
	});

	/** The primary key of the existing item the input shows; a pending create has none yet. */
	const itemKey = computed<CompositePrimaryKey | null>(() => {
		const shown = item.value;
		if (isCreate.value || !shown || !collection.value) return null;
		const key = getOriginalItemKey(shown) ?? pickByPrimitiveFields(shown, getCollectionPrimaryKeyFields(collection.value));
		return isEmptyKey(key) ? null : key;
	});

	return { collection, item, itemKey, effectiveKey, isCreate, isUpdate, isLoading };
};
